import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface QuestionnaireStatus {
  hasStarted: boolean;
  isCompleted: boolean;
  currentStep: number;
  responseId: string | null;
}

export function useQuestionnaireStatus() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["questionnaire-status", user?.id],
    queryFn: async (): Promise<QuestionnaireStatus> => {
      if (!user) {
        return {
          hasStarted: false,
          isCompleted: false,
          currentStep: 0,
          responseId: null,
        };
      }

      // Check profile for questionnaire_completed flag first
      const { data: profile } = await supabase
        .from("profiles")
        .select("questionnaire_completed")
        .eq("id", user.id)
        .single();

      if (profile?.questionnaire_completed) {
        return {
          hasStarted: true,
          isCompleted: true,
          currentStep: 0,
          responseId: null,
        };
      }

      // Check for existing questionnaire response
      const { data: response } = await supabase
        .from("questionnaire_responses")
        .select("id, status, current_step")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!response) {
        return {
          hasStarted: false,
          isCompleted: false,
          currentStep: 0,
          responseId: null,
        };
      }

      return {
        hasStarted: true,
        isCompleted: response.status === "submitted",
        currentStep: response.current_step || 1,
        responseId: response.id,
      };
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
}
